import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { CREATE_EVENT_MUTATION, GET_EVENT_TYPES, GET_ALL_EVENTS, GET_ALL_EMPLOYEES } from "../../graphql/queries";

interface CreateEventDialogProps {
  open: boolean;
  onClose: () => void;
}

export const CreateEventDialog: React.FC<CreateEventDialogProps> = ({
  open,
  onClose,
}) => {
  const [employeeId, setEmployeeId] = useState("");
  const [eventTypeId, setEventTypeId] = useState("");
  const [note, setNote] = useState("");
  const [timestamp, setTimestamp] = useState("");

  const { data: eventTypesData } = useQuery(GET_EVENT_TYPES);
  const { data: employeesData } = useQuery(GET_ALL_EMPLOYEES);
  const eventTypes = eventTypesData?.eventTypes || [];
  const employees = employeesData?.employees || [];

  const [createEvent, { loading: creating }] = useMutation(CREATE_EVENT_MUTATION, {
    refetchQueries: [{ query: GET_ALL_EVENTS }],
    onCompleted: () => {
      resetForm();
      onClose();
    },
    onError: (error) => {
      console.error("Error creating event:", error);
    },
  });

  useEffect(() => {
    if (open) {
      // Set current date and time as default when dialog opens
      const now = new Date();
      const formattedTimestamp = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setTimestamp(formattedTimestamp);
    }
  }, [open]);

  const resetForm = () => {
    setEmployeeId("");
    setEventTypeId("");
    setNote("");
    setTimestamp("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!employeeId || !eventTypeId || !timestamp) {
      return;
    }

    try {
      await createEvent({
        variables: {
          employeeId,
          eventTypeId,
          note: note || null,
          timestamp: new Date(timestamp).toISOString(),
        },
      });
    } catch (error) {
      console.error("Error creating event:", error);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Dodaj nowe wydarzenie</DialogTitle>
          <DialogDescription>
            Utwórz nowe wydarzenie dla wybranego pracownika
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="timestamp">Data i godzina</Label>
            <Input
              id="timestamp"
              type="datetime-local"
              value={timestamp}
              onChange={(e) => setTimestamp(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="employee">Pracownik</Label>
            <select
              id="employee"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              required
            >
              <option value="">Wybierz pracownika</option>
              {[...employees]
                .sort((a: any, b: any) =>
                  a.lastName.localeCompare(b.lastName, 'pl') ||
                  a.firstName.localeCompare(b.firstName, 'pl')
                )
                .map((employee: any) => (
                <option key={employee.id} value={employee.id}>
                  {employee.lastName} {employee.firstName}
                  {employee.department && ` - ${employee.department}`}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="eventType">Typ wydarzenia</Label>
            <select
              id="eventType"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={eventTypeId}
              onChange={(e) => setEventTypeId(e.target.value)}
              required
            >
              <option value="">Wybierz typ wydarzenia</option>
              {eventTypes.map((type: any) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Notatka</Label>
            <Textarea
              id="note"
              placeholder="Dodaj notatkę (opcjonalnie)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Anuluj
            </Button>
            <Button type="submit" disabled={creating}>
              {creating ? "Dodawanie..." : "Dodaj wydarzenie"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};